import { ApolloError } from "@apollo/client";
import Router from "next/router";

/**
 * ErrorCode are codes returned from the backend to identify a specific error.
 *
 * The following is a list of well-known error codes used by the backend and the frontend.
 *
 * Please follow these rules:
 * - once an error code is defined, do not change it;
 * - if you want to change an error code, define a new one and deprecate the old one;
 * - if you want to remove an error code, remove it from the backend first, then the frontend (in the following release);
 * - keep this list in sync with backend/go/errors/apperror.go
 *
 * @description The error code represents errors that occur during backend processing.
 */
export enum ErrorCode {
  INTERNAL_SERVER_ERROR,
  ACCESS_DENIED,
  LOGIN_REQUIRED_TO_VERIFY_EMAIL,
  BIZSOL_INTERNAL_SERVER_ERROR,
  CONNBOARD_INTERNAL_SERVER_ERROR,
  FAILED_TO_VERIFY_EMAIL_ADDRESS,

  // appErrors from BE.
  INVALID_REQUEST_PARAMETERS,
  INVALID_REQUEST_HEADERS,
  INVALID_PASSWORD_RESET_TOKEN,
  INVALID_EMAIL_AUTH_TOKEN,
  ADMIN_AUTHORITY_LAST_ONE_PERSON,
  USERNAME_EXISTS,
  INVALID_PASSWORD,
  ONLY_ADMINISTRATOR_CAN_OPERATE_IT,
  USER_IS_ALREADY_MEMBER,
  ADMINISTRATOR_CANNOT_BE_DELETED,
  MEMBER_CANNOT_REGISTER_EMAIL,
  EMAIL_EXISTS,
  ITEM_NOT_FOUND,
  EMAIL_NOT_EXISTS,
  INVALID_COMPANY,
  NO_REGISTRATION_AUTHORITY,
  USER_NOT_EXISTS,
  USER_IS_DISABLED,
  NO_REGISTRATION_COMPANY_INFO,
  BIZSOL_COMPANY_ALREADY_REGISTERED,
  BIZSOL_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY,
  BIZSOL_USER_IS_NOT_ALLOWED_TO_SIGN_IN,
  BIZSOL_USER_IS_NOT_ALLOWED_TO_CONNECT,
  BIZSOL_USER_IS_NOT_ALLOWED_TO_BE_REGISTERED,
  BIZSOL_TOKEN_IS_EXPIRED,
  BIZSOL_INVALID_ID_TOKEN,
  BIZSOL_INVALID_REGISTRATION_TOKEN,
  BIZSOL_USER_IS_ALREADY_CONNECTED,
  BIZSOL_USER_IS_ALREADY_WAITING,
  BIZSOL_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT,
  BIZSOL_USER_IS_ALREADY_CONNECTED_TO_ANOTHER_MIKATANO_ID,
  BIZSOL_ID_LINK_SWITCHING_IS_NOT_ALLOWED,
  BIZSOL_USER_IS_NOT_LINKED_WITH_ANY_MIKATANO_ID,
  BIZSOL_REQUEST_FAILED,
  SIGNED_IN_AS_ANOTHER_USER,
  CONNBOARD_USER_IS_ALREADY_CONNECTED,
  CONNBOARD_REQUEST_FAILED,
  CONNBOARD_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT,
  CONNBOARD_COMPANY_ALREADY_REGISTERED,
  CONNBOARD_INVALID_REGISTRATION_TOKEN,
  CONNBOARD_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY,
  CONNBOARD_USER_IS_NOT_ALLOWED_TO_SIGN_IN,
  CONNBOARD_USER_IS_NOT_ALLOWED_TO_CONNECT,
  CONNBOARD_TOKEN_IS_EXPIRED,
  CONNBOARD_INVALID_ID_TOKEN,
  BANK_PORTAL_COMPANY_INFO_IS_ALREADY_REGISTERED_ERROR,
  BUSINESS_LOAN_IS_IN_USE,
}

/**
 * Error messages from Backend GraphQL API.
 * @description The error messages correspond to extensions.code of AppoloError.
 */
const errorMessages = new Map<ErrorCode, string>([
  [
    ErrorCode.ADMIN_AUTHORITY_LAST_ONE_PERSON,
    "権限の変更に失敗しました。管理者権限最後の１人です。",
  ],
  [
    ErrorCode.USERNAME_EXISTS,
    "入力されたユーザーIDは既に利用されています。他のユーザー名をお試しください。",
  ],
  [
    ErrorCode.INVALID_PASSWORD,
    "パスワードには半角英大文字、英小文字、数字、記号がそれぞれ1文字以上含まれる必要があります。",
  ],
  [
    ErrorCode.ONLY_ADMINISTRATOR_CAN_OPERATE_IT,
    "権限がありません。事業者内のMikatano シリーズ管理者にお問い合わせください。",
  ],
  [ErrorCode.USER_IS_ALREADY_MEMBER, "既に一般ユーザーです。"],
  [
    ErrorCode.ADMINISTRATOR_CANNOT_BE_DELETED,
    "管理ユーザーを削除することは出来ません。",
  ],
  [
    ErrorCode.MEMBER_CANNOT_REGISTER_EMAIL,
    "一般ユーザーはメールアドレスの登録は出来ません。",
  ],
  [
    ErrorCode.EMAIL_EXISTS,
    "入力されたメールアドレスは既に利用されています。他のメールアドレスをお試しください。",
  ],
  [
    ErrorCode.ITEM_NOT_FOUND,
    "値を取得出来ませんでした。再度、登録をおこなって下さい。",
  ],
  [
    ErrorCode.INVALID_COMPANY,
    "予期せぬ操作により登録・変更は行えませんでした。再度、登録・変更をおこなって下さい。",
  ],
  [
    ErrorCode.NO_REGISTRATION_AUTHORITY,
    "予期せぬ操作により登録・変更は行えませんでした。再度、登録・変更をおこなって下さい。",
  ],
  [
    ErrorCode.USER_NOT_EXISTS,
    "指定されたユーザーは存在しません。再度、操作をおこなって下さい。",
  ],
  [
    ErrorCode.USER_IS_DISABLED,
    "指定されたユーザーは存在しません。再度、操作をおこなって下さい。",
  ],
  [
    ErrorCode.BIZSOL_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT,
    "ログイン状態が変更されたためログアウトしました。お手数ですが、再度ログイン操作をお願いします。",
  ],
  [
    ErrorCode.BANK_PORTAL_COMPANY_INFO_IS_ALREADY_REGISTERED_ERROR,
    "既に事業者情報は登録されています。お手数ですが再度トップページよりアクセスしてください。 ",
  ],
  [
    ErrorCode.BUSINESS_LOAN_IS_IN_USE,
    "「融資ポータル」機能をご利用中のため、退会出来ません",
  ],
]);

export const InternalServerErrorMessage =
  "メンテナンス中、または何らかの障害が発生している可能性があります。メンテナンス終了もしくは復旧までしばらくお待ちください。";

/**
 * Check if the error code is included in the ApolloError.
 * @param {ApolloError} err
 * @param {ErrorCode} errorCode
 * @returns {boolean} true if the error code is included in the ApolloError.
 */
export const errorCodeIsIncluded = (
  err: ApolloError,
  errorCode: ErrorCode
): boolean => {
  var included = false;
  err.graphQLErrors.forEach((element) => {
    const extensions = element.extensions;
    if (!extensions) {
      return;
    }
    const code = extensions.code;
    if (typeof code === "string") {
      if (ErrorCode[code] == errorCode) {
        included = true;
        return;
      }
    }
  });
  return included;
};

/**
 * extract extra info from apollo error
 * @param {ApolloError} err apollo error
 * @param {string} key extra info key from BackendErrorExtraInfoKeys class
 */
export const extractErrExtraInfo = (err: ApolloError, key: string): string => {
  var info = "";
  err.graphQLErrors.forEach((element) => {
    const extensions = element.extensions;
    if (!extensions) {
      return;
    }
    const extraInfo = extensions[key];
    if (typeof extraInfo === "string") {
      info = extraInfo;
    }
  });
  return info;
};

/**
 * extract error message from apollo error for Bizsol request
 * @param {ApolloError} err apollo error
 * @returns {string} error message
 */
export const extractBizsolRqeustErrorMessage = (err: ApolloError): string => {
  var msg = "";
  const targetCode = ErrorCode[ErrorCode.BIZSOL_REQUEST_FAILED] + ": ";
  err.graphQLErrors.forEach((element) => {
    const extensions = element.extensions;
    if (!extensions) {
      return;
    }
    const code = extensions.code;
    if (typeof code === "string") {
      if (code.startsWith(targetCode)) {
        msg = code.substring(targetCode.length);
      }
    }
  });
  return msg;
};

/**
 * extract error message from apollo error for ConnBoard request
 * @param {ApolloError} err apollo error
 * @returns {string} error message
 */
export const extractConnBoardRqeustErrorMessage = (
  err: ApolloError
): string => {
  var msg = "";
  const targetCode = ErrorCode[ErrorCode.CONNBOARD_REQUEST_FAILED] + ": ";
  err.graphQLErrors.forEach((element) => {
    const extensions = element.extensions;
    if (!extensions) {
      return;
    }
    const code = extensions.code;
    if (typeof code === "string") {
      if (code.startsWith(targetCode)) {
        msg = code.substring(targetCode.length);
      }
    }
  });
  return msg;
};

/**
 * extract error message from apollo error
 * @param {ApolloError} err apollo error
 * @returns {string} error message
 * @description [TODO] Only the first error is processed. Please fix it.
 */
// FIXME: 先頭のエラーのみ処理している
export const getErrorCode = (err: ApolloError): ErrorCode => {
  if (err.graphQLErrors.length > 0 && err.graphQLErrors[0].extensions) {
    const str = err.graphQLErrors[0].extensions.code;
    if (typeof str === "string") {
      return ErrorCode[str];
    }
  }
};

/**
 * Get error message from ApolloError
 * @param {ApolloError} err
 * @param {string} defaultMessage
 * @returns {string} error message
 */
export const getErrorMessage = (
  err: ApolloError,
  defaultMessage: string
): string => {
  const code = getErrorCode(err); // 先頭のエラーのみ処理するため注意
  const message = errorMessages.get(code);
  if (message) {
    return message;
  }
  return defaultMessage;
};

export const errorPagePath = (code: ErrorCode): string => {
  return "/error/" + code;
};

export const moveToErrorPage = (code?: ErrorCode) => {
  const errorCode = code ? code : ErrorCode.INTERNAL_SERVER_ERROR;
  Router.push({
    pathname: errorPagePath(errorCode),
  });
};

export const moveToBizsolRequestErrorPage = (message: string) => {
  Router.push({
    pathname: "/error/" + ErrorCode.BIZSOL_REQUEST_FAILED,
    query: { msg: message },
  });
};

export const moveToConnBoardRequestErrorPage = (message: string) => {
  Router.push({
    pathname: "/error/" + ErrorCode.CONNBOARD_REQUEST_FAILED,
    query: { msg: message },
  });
};

/**
 * Backend error extra information key definitions
 * @description The key definitions correspond to extensions.$key of AppoloError. see: backend/go/apperror/apperror.go
 * @example const extraInfo = err.graphQLErrors[0].extensions.$key;
 */
export class BackendErrorExtraInfoKeys {
  static readonly BIZSOL_SUB = "bizsol_sub";
}

// =================================================================================================
// # Suggestions for Frontend Error codes and messages management (We need to discuss this)
// ja:
// - ErrorCode, getErrorMessages などは Backend のErrorを扱うためのものであり AppoloError を交えた処理になっている
// - Frontend でのエラーコードとメッセージは、FrontendErrorCodes, FrontendErrorMsgs などで定義し、
// - AppoloError に依存しないようにする
// en:
// - ErrorCode, getErrorMessages, etc. are for handling Backend errors and involve processing with AppoloError
// - Define error codes and messages for Frontend in FrontendErrorCodes, FrontendErrorMsgs, etc.
// - Make sure not to depend on AppoloError
//
// e.g.
// export enum FrontendErrorCodes {
//   INTERNAL_ERR_FEATURE_FLAG_LOADING = 1000,
//   INTERNAL_ERR_SOME_OTHER_ERROR = 1001,
// }

// export const FrontendErrors: Record<FrontendErrorCodes, string> = {
//   [FrontendErrorCodes.INTERNAL_ERR_FEATURE_FLAG_LOADING]:
//     "エラーが発生しました。ページをリロードしてください。",
//   [FrontendErrorCodes.INTERNAL_ERR_SOME_OTHER_ERROR]: "Some other error message",
// };

// export class FrontendErrorUtil {
//   static getErrorMessage(code: FrontendErrorCodes): string {
//     const errMsg = FrontendErrors[code];
//     return errMsg ? `${errMsg} - code: ${code}` : `エラーが発生しました。 - code: ${code}`
//   }
// }

// // usage example
// // Usage example
// try {
//   // Some code that might throw an error
//   throw new Error("Some error");
// } catch (err) {
//   const errorCode = FrontendErrorCodes.INTERNAL_ERR_FEATURE_FLAG_LOADING;
//   const errorMessage = FrontendErrorUtil.getErrorMessage(errorCode);
//   console.error(errorMessage);
// }

// =================================================================================================

/**
 * Frontend error messages
 * @description This error message represents errors that occur during frontend checks.
 */
export class FrontendErrorMsgs {
  static readonly COUPON_CODE_NOT_AVAILABLE =
    "入力されたクーポンコードは利用できません。";
  static readonly CONTRACT_ADMIN_CANNOT_BE_DELETED =
    "契約管理者ユーザーを削除することは出来ません。";
  static readonly ADMIN_CANNOT_BE_DELETED =
    "管理者ユーザーを削除することは出来ません。";
}
